import { z } from 'zod';
import type { Result } from './result';
import { toContractError, type ContractError } from './errors';
import { QueueReminderStateSchema } from './push';
import { UuidSchema } from './uuid';

export const QueueInfoSchema = z.object({
  position: z.number(),
  eta_seconds: z.number().optional(),
});

export const WebRTCConfigSchema = z.object({
  client_id: z.string().optional(),
  stun: z.array(z.union([z.string(), z.record(z.unknown())])).optional(),
  turn: z.array(z.union([z.string(), z.record(z.unknown())])).optional(),
});

export const AssignClientMetaSchema = z.object({
  device: z.string().optional(),
  locale: z.string().optional(),
  timezone: z.string().optional(),
});

export const AssignAttributionSchema = z.object({
  attribution_token: z.string().min(1).optional(),
});

export const AssignRequestSchema = z.object({
  game_type_key: z.string().optional(),
  robot_id: UuidSchema,
  client_meta: AssignClientMetaSchema.optional(),
  attribution: AssignAttributionSchema.optional(),
  force_lease_refresh: z.boolean().optional(),
});

const AssignmentAssignedSchema = z.object({
  status: z.literal('assigned'),
  ticket_id: UuidSchema,
  session_id: UuidSchema,
  robot_id: UuidSchema,
  room_id: z.string(),
  lease_token: z.string().optional(),
  lease_expires_at: z.string().nullable().optional(),
  reminder: QueueReminderStateSchema.optional(),
});

const AssignmentOfferedSchema = z.object({
  status: z.literal('offered'),
  ticket_id: UuidSchema,
  robot_id: UuidSchema.optional(),
  room_id: z.string().optional(),
  offer_expires_at: z.string().nullable().optional(),
  reminder: QueueReminderStateSchema.optional(),
});

const AssignmentQueuedSchema = z.object({
  status: z.literal('queued'),
  ticket_id: UuidSchema,
  queue: QueueInfoSchema,
  session_id: UuidSchema.optional().nullable(),
  reminder: QueueReminderStateSchema.optional(),
});

export const AssignmentResponseSchema = z.discriminatedUnion('status', [
  AssignmentAssignedSchema,
  AssignmentOfferedSchema,
  AssignmentQueuedSchema,
]);

export const QueueResponseSchema = z.discriminatedUnion('status', [
  AssignmentQueuedSchema,
  AssignmentOfferedSchema,
  AssignmentAssignedSchema,
]);

export const RobotInfoSchema = z.object({
  id: UuidSchema,
  name: z.string().optional().nullable(),
  serial_number: z.string().optional().nullable(),
  model: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  type: z.string().optional().nullable(),
  location_id: z.number().optional().nullable(),
});

export const AvailabilityResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('live'),
    robot_id: UuidSchema,
    room_id: z.string().optional().nullable(),
    reason: z.string().optional().nullable(),
  }),
  z.object({
    status: z.literal('maintenance'),
    reason: z.string().optional().nullable(),
  }),
]);

export const PublicPreviewResponseSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('live'),
    robot_id: UuidSchema,
    room_id: z.string(),
    queue_depth: z.number(),
    active_session: z.boolean(),
    reason: z.string().optional().nullable(),
  }),
  z.object({
    mode: z.literal('recorded'),
    robot_id: UuidSchema.optional().nullable(),
    room_id: z.string().optional().nullable(),
    queue_depth: z.number(),
    active_session: z.boolean(),
    reason: z.string().optional().nullable(),
  }),
  z.object({
    mode: z.literal('maintenance'),
    robot_id: UuidSchema.optional().nullable(),
    room_id: z.string().optional().nullable(),
    queue_depth: z.number(),
    active_session: z.boolean(),
    reason: z.string().optional().nullable(),
  }),
]);

export const LiveKitTokenResponseSchema = z.object({
  url: z.string(),
  token: z.string(),
  room_id: z.string(),
  identity: z.string(),
});

export type QueueInfo = z.infer<typeof QueueInfoSchema>;
export type WebRTCConfig = z.infer<typeof WebRTCConfigSchema>;
export type AssignClientMeta = z.infer<typeof AssignClientMetaSchema>;
export type AssignAttribution = z.infer<typeof AssignAttributionSchema>;
export type AssignRequest = z.infer<typeof AssignRequestSchema>;
export type AssignmentResponse = z.infer<typeof AssignmentResponseSchema>;
export type QueueResponse = z.infer<typeof QueueResponseSchema>;
export type RobotInfo = z.infer<typeof RobotInfoSchema>;
export type AvailabilityResponse = z.infer<typeof AvailabilityResponseSchema>;
export type PublicPreviewResponse = z.infer<typeof PublicPreviewResponseSchema>;
export type LiveKitTokenResponse = z.infer<typeof LiveKitTokenResponseSchema>;

export const parseApiResponse = <T>(
  raw: unknown,
  schema: z.ZodType<T>,
  context?: { source?: 'http'; type?: string }
): Result<T, ContractError> => {
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      err: toContractError(parsed.error, { source: context?.source ?? 'http', type: context?.type }),
    };
  }
  return { ok: true, value: parsed.data };
};
